// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type * as CreateJS from '@suntex.classic/createjs';
import {
  Color,
  getGradientColor,
  toHexString,
  toRGBA,
} from '@suntex.core/color';

const DEFAULT_SHADOW_ALPHA = 0.1;
const DEFAULT_SHADOW_BLUR = 2.0;

export type Sprite = createjs.Shape | createjs.Graphics;

/**
 * Create a new CreateJS Shape (sprite)
 */
export function createSprite(
  this: void,
  draw?: (graphics: createjs.Graphics) => void,
): createjs.Shape {
  const sprite = new createjs.Shape();
  draw?.(sprite.graphics);
  return sprite;
}

/**
 * Add a shadow to a movie clip
 */
export function addShadow(
  this: void,
  movieClip: createjs.DisplayObject,
  opts?: { alpha?: number; blur?: number; offset?: number },
): void {
  const alpha = opts?.alpha ?? DEFAULT_SHADOW_ALPHA;
  movieClip.shadow = new createjs.Shadow(
    `rgba(0,0,0,${opts?.alpha ?? alpha})`,
    opts?.offset ?? 2,
    opts?.offset ?? 2,
    opts?.blur ?? DEFAULT_SHADOW_BLUR,
  );
}

/**
 * @deprecated Use `addShadow`
 */
export const shadow = addShadow;

/* Drawing */

/**
 * Begin a fill on a sprite
 */
export function beginFill(sprite: createjs.Shape, color: Color): void {
  sprite.graphics.beginFill(toHexString(color));
}

/**
 * Draw a rectangle on a sprite
 */
export function drawRect(
  sprite: createjs.Shape,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  sprite.graphics.drawRect(x, y, width, height);
}

/**
 * Draw a circle on a sprite
 */
export function drawCircle(
  sprite: createjs.Shape,
  radius: number,
  x = 0,
  y = 0,
): void {
  sprite.graphics.drawCircle(x, y, radius);
}

/**
 * Draw a polygon on a sprite
 */
export function drawPolygon(
  sprite: Sprite,
  numPoints: number,
  radius: number,
  angle = 0,
  x = 0,
  y = 0,
): void {
  const g = getGraphics(sprite);
  const angleIncrement = (2 * Math.PI) / numPoints;

  for (let i = 0; i < numPoints; i++) {
    const currentAngle = angle + i * angleIncrement;
    const px = x + radius * Math.cos(currentAngle);
    const py = y + radius * Math.sin(currentAngle);

    if (i === 0) {
      g.moveTo(px, py);
    } else {
      g.lineTo(px, py);
    }
  }

  // Close the polygon
  const firstAngle = angle;
  const firstX = x + radius * Math.cos(firstAngle);
  const firstY = y + radius * Math.sin(firstAngle);
  g.lineTo(firstX, firstY);
}

/* eslint-disable no-magic-numbers */

export function drawButtonBackground(
  sprite: Sprite,
  radius: number,
  color: Color,
  intensity?: number,
): void {
  const g = getGraphics(sprite);
  const highlightColor = getGradientColor(color, 'highlight', intensity);
  const shadowColor = getGradientColor(color, 'shadow', intensity);
  g.beginRadialGradientFill(
    [toHexString(highlightColor), toHexString(shadowColor)],
    [0, 1],
    0,
    -radius / 8,
    radius / 2,
    0,
    radius / 8,
    radius,
  ).drawCircle(0, 0, radius);
}

export function drawButtonShine(
  sprite: Sprite,
  radius: number,
  color: Color,
  intensity?: number,
): void {
  const g = getGraphics(sprite);
  const highlightColor = getGradientColor(color, 'highlight');
  g.beginLinearGradientFill(
    [toRGBA(highlightColor, intensity), toRGBA(color, 0)],
    [0, 0.5],
    0,
    -radius,
    0,
    radius,
  );
  g.drawCircle(0, -radius * 0.2, radius * 0.75);
}

function getGraphics(sprite: Sprite): createjs.Graphics {
  return sprite instanceof createjs.Shape ? sprite.graphics : sprite;
}

/* eslint-enable no-magic-numbers */
